// Per-repo sealed secrets data layer (arc 101 slice 06, doc 06). Wraps
// `GET/PUT/DELETE /v1/repos/:owner/:repo/secrets`
// (`apps/api/src/routes/repo-secrets.ts`).
//
// Addressed by FULL NAME, not by repo uuid (#1851): while the collection sat at
// `/v1/repos/:repoId/secrets` its GET was swallowed by `GET /v1/repos/:owner/:repo`,
// so this tab's list read 404'd for its entire life. The `/` in `owner/repo` is the
// path separator, so only the SEGMENTS are encoded — the `kbPagePath` rule.
//
// A read is metadata ONLY — name + exposure + environment + timestamps — by
// design: the route's `toView` has no value field, because the query behind it
// never projects the ciphertext column. `RepoSecretRow` mirrors that shape
// exactly so there is no field here that could ever carry a value.
//
// The write sends the raw pasted `.env` text as `dotenv`, not a client-parsed
// `secrets` array: `parseDotenv` on the server is the one parser (its own
// comment: "a pasted `.env` parses to exactly what the runner's writer would
// emit"), so re-implementing it here would be the exact "invent a shape"
// anti-pattern this slice is supposed to avoid. `repo-secrets.test.ts` exercises
// the `dotenv` form directly, so it is a real, tested path.

import type { CredentialExposure, SecretEnvironment } from '@developerz/domain';
import { createQuery } from '@tanstack/solid-query';
import { activeAccountId } from './active-account';
import { apiFetch } from './api';
import type { Page } from './paged';
import { SESSION_TOKEN } from './session';

/** `toView` in `repo-secrets.ts` — structurally cannot carry a value. */
export type RepoSecretRow = {
  readonly name: string;
  readonly exposure: CredentialExposure;
  readonly environment: SecretEnvironment;
  readonly created_at: string;
  readonly updated_at: string;
};

export const REPO_SECRETS_QUERY_KEY = 'repo-secrets';

/**
 * `owner/repo` → this repo's secrets collection. REFUSES anything that is not
 * exactly two non-empty segments, because the failure mode here is not an error
 * but a WRONG SUCCESS: `'a/b/extra'.split('/')` destructured to two names keeps
 * `a/b` and silently drops the rest, so a caller would read, rotate or delete a
 * DIFFERENT repo's vault and get a 200 saying it worked. A missing segment is
 * the other half (`/v1/repos/owner//secrets` is a different route shape).
 *
 * Today's only caller passes the API's own `full_name`, so nothing reachable is
 * malformed — which is precisely why this fails loudly rather than falling back:
 * a silent truncation on a credential surface would surface as someone else's
 * secrets, not as a bug report. Only the SEGMENTS are encoded; the separator
 * stays literal (the `kbPagePath` rule).
 */
function secretsPath(fullName: string): string {
  const [owner, repo, ...rest] = fullName.split('/');
  if (owner === undefined || repo === undefined || rest.length > 0 || owner === '' || repo === '') {
    throw new Error(
      `repo secrets: expected an "owner/repo" full name, got ${JSON.stringify(fullName)}`,
    );
  }
  return `/v1/repos/${encodeURIComponent(owner)}/${encodeURIComponent(repo)}/secrets`;
}

/**
 * The repo's stored secrets (metadata only). Idle until a real `owner/repo` lands.
 *
 * The READ is the canonical `{data, next_cursor}` envelope with `next_cursor`
 * always null (a repo's vault is served whole), so no Load-more. The WRITE below
 * is not: a PUT echoes the batch it stored under `secrets`, which is a mutation
 * result rather than a collection read, and the API deliberately left it alone.
 */
export function useRepoSecrets(fullName: () => string, token: string | null = SESSION_TOKEN) {
  return createQuery<Page<RepoSecretRow>>(() => ({
    queryKey: [REPO_SECRETS_QUERY_KEY, fullName(), token, activeAccountId()],
    enabled: token !== null && fullName() !== '',
    queryFn: () => apiFetch<Page<RepoSecretRow>>(secretsPath(fullName()), token),
  }));
}

export type PutRepoSecretsDotenvInput = {
  readonly environment: SecretEnvironment;
  readonly exposure: CredentialExposure;
  /** The whole pasted `.env` — parsed server-side, never here. */
  readonly dotenv: string;
};

/**
 * Store a whole pasted `.env` for one environment. Re-PUT rotates the same
 * names in place. A malformed paste is refused whole (422, `detail` names the
 * line — never the value) via `errorReason`.
 */
export async function putRepoSecretsDotenv(
  fullName: string,
  input: PutRepoSecretsDotenvInput,
  token: string | null = SESSION_TOKEN,
): Promise<{ secrets: readonly RepoSecretRow[] }> {
  return apiFetch<{ secrets: readonly RepoSecretRow[] }>(secretsPath(fullName), token, {
    method: 'PUT',
    body: JSON.stringify(input),
  });
}

/** Remove one name from one environment, or from every environment when omitted. */
export async function deleteRepoSecret(
  fullName: string,
  name: string,
  environment: SecretEnvironment | undefined,
  token: string | null = SESSION_TOKEN,
): Promise<{ name: string; environments: readonly SecretEnvironment[] }> {
  const query = environment !== undefined ? `?environment=${encodeURIComponent(environment)}` : '';
  return apiFetch(`${secretsPath(fullName)}/${encodeURIComponent(name)}${query}`, token, {
    method: 'DELETE',
  });
}
